import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ViewProgrammePage() {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const [programme, setProgramme] = useState(null);
  const [fullProgramme, setFullProgramme] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [viewFullProgramme, setViewFullProgramme] = useState(false);

  useEffect(() => {
    loadData();
  }, [competitionId]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load competition details
      const compResp = await axios.get(`${API}/competitions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const comp = compResp.data.find(c => c.id === competitionId);
      setCompetition(comp);
      
      // Load club programme (filtered)
      const progResp = await axios.get(`${API}/club/programme/${competitionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgramme(progResp.data);
      
      // Load full programme (all gymnasts)
      const fullProgResp = await axios.get(`${API}/club/programme/${competitionId}/full`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFullProgramme(fullProgResp.data);
    } catch (error) {
      console.error('Failed to load programme:', error);
      toast.error('Failed to load programme');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/club/programme/${competitionId}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gymnast_numbers_${competition?.title || 'programme'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-600">Loading programme...</p>
      </div>
    );
  }

  const displayProgramme = viewFullProgramme ? fullProgramme : programme;

  // Check if programme is hidden
  if (!loading && programme?.message === "Programme not yet published") {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-gray-600 text-lg">
                📋 Programme not yet published
              </p>
              <p className="text-center text-gray-500 text-sm mt-2">
                The programme for this competition will be available soon.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Programme
              </h1>
              <p className="text-gray-600 mt-1">{competition?.title}</p>
            </div>
            
            {programme?.rounds?.length > 0 && (
              <Button
                onClick={handleExportPDF}
                disabled={downloading}
                className="bg-[#000000] hover:bg-[#333333]"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading ? 'Generating...' : 'Export Gymnast Numbers'}
              </Button>
            )}
          </div>

          {/* Toggle View */}
          {programme?.rounds?.length > 0 && (
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center justify-center gap-4">
                  <Label 
                    htmlFor="view-toggle" 
                    className={`cursor-pointer text-sm font-semibold transition-colors ${!viewFullProgramme ? 'text-[#000000]' : 'text-gray-500'}`}
                  >
                    View My Club Programme
                  </Label>
                  <Switch
                    id="view-toggle"
                    checked={viewFullProgramme}
                    onCheckedChange={setViewFullProgramme}
                    className="data-[state=checked]:bg-[#000000]"
                  />
                  <Label 
                    htmlFor="view-toggle" 
                    className={`cursor-pointer text-sm font-semibold transition-colors ${viewFullProgramme ? 'text-[#000000]' : 'text-gray-500'}`}
                  >
                    View Full Programme
                  </Label>
                </div>
                <p className="text-xs text-gray-600 text-center mt-3">
                  {viewFullProgramme 
                    ? 'Showing all rounds, groups, and clubs participating' 
                    : 'Showing only rounds and groups with your gymnasts'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Programme Content */}
        {!displayProgramme || displayProgramme.rounds.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-gray-500">
                No programme available for this competition yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {displayProgramme.rounds.map((round, roundIndex) => (
              <Card key={roundIndex}>
                <CardHeader>
                  <CardTitle>{round.roundName}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {round.groups.map((group, groupIndex) => (
                      <div key={groupIndex} className="border rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-3">{group.groupName}</h3>
                        <div className="space-y-2">
                          {group.gymnasts.map((gymData, gymIndex) => (
                            <div
                              key={gymIndex}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded"
                            >
                              <div className="flex items-center space-x-4">
                                <div className="bg-[#000000] text-white font-bold rounded px-3 py-1 text-lg">
                                  {gymData.number}{gymData.letter}
                                </div>
                                <div>
                                  <p className="font-medium">{gymData.gymnastName || `Gymnast #${gymIndex + 1}`}</p>
                                  <p className="text-xs text-gray-600">
                                    {gymData.clubName || 'Unknown Club'} • {gymData.category || 'N/A'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                          {group.gymnasts.length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-2">
                              No gymnasts assigned to this group yet.
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
